/**
 * CR043: Quality Flag Chips Component
 */

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { READ_QUALITY_FLAGS, type ReadQualityFlag } from "@/lib/read-phase/types";

interface ReadQualityFlagChipsProps {
  flags: string[] | null;
  maxVisible?: number;
}

const FLAG_COLORS: Record<string, string> = {
  CONTENT_OK: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  COOKIE_MODAL_PRESENT: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  COOKIE_TEXT_DOMINANT: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  LOCATION_GATE_PRESENT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  LOCATION_NOT_SET: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  NAV_HEADER_ONLY: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  LOW_TEXT_LENGTH: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  JS_CONTENT_NOT_RENDERED: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  IMAGE_HEAVY_PAGE: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  PDF_DETECTED: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  PDF_TEXT_EMPTY: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  OCR_USED: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  CAPTCHA_OR_BOT_BLOCK: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  NETWORK_TIMEOUT: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export function ReadQualityFlagChips({ flags, maxVisible = 3 }: ReadQualityFlagChipsProps) {
  if (!flags || flags.length === 0) {
    return <span className="text-muted-foreground text-xs">-</span>;
  }

  const visibleFlags = flags.slice(0, maxVisible);
  const hiddenCount = flags.length - maxVisible;

  return (
    <TooltipProvider>
      <div className="flex flex-wrap gap-1">
        {visibleFlags.map((flag) => {
          const tooltip = READ_QUALITY_FLAGS[flag as ReadQualityFlag] || flag;
          const colorClass = FLAG_COLORS[flag] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
          
          return (
            <Tooltip key={flag}>
              <TooltipTrigger asChild>
                <Badge variant="outline" className={`text-xs py-0 px-1.5 ${colorClass}`}>
                  {flag.replace(/_/g, ' ').slice(0, 15)}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
        {hiddenCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="text-xs py-0 px-1.5">
                +{hiddenCount}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <div className="space-y-1">
                {flags.slice(maxVisible).map((flag) => (
                  <div key={flag} className="text-xs">
                    {READ_QUALITY_FLAGS[flag as ReadQualityFlag] || flag}
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
